import { Component, Suspense, lazy, type ComponentProps, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { modelLoadsByItself } from './crag-model-framing';

// three.js and the model: only somebody who opens one pays for them.
const CragRouteModel = lazy(() => import('../crag/CragRouteModel'));

/** A chunk that did not arrive, which at a crag is a real possibility, is a failed model. */
class ChunkBoundary extends Component<{ onError: () => void; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    this.props.onError();
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

type ModelProps = ComponentProps<typeof CragRouteModel>;

interface CragModelCanvasProps
  extends Pick<ModelProps, 'url' | 'routes' | 'framing' | 'selectedRouteId' | 'viewLimits' | 'onSelectRoute'> {
  byteSize: number | null;
  loadRequested: boolean;
  onLoadRequested: () => void;
  onFailed: () => void;
}

/**
 * The model as a member reads it, wherever it is on the page: fetched by itself up to
 * a size, past which it waits to be asked, since signal is what is scarce under a cliff.
 */
export function CragModelCanvas({
  url,
  byteSize,
  loadRequested,
  onLoadRequested,
  onFailed,
  routes,
  framing,
  selectedRouteId,
  viewLimits,
  onSelectRoute,
}: CragModelCanvasProps) {
  const { t } = useTranslation();

  if (!loadRequested && !modelLoadsByItself(byteSize)) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <Button variant="secondary" className="gap-2" onClick={onLoadRequested}>
          <Box className="h-4 w-4" />
          {t('cragProfile.model3d.load', { size: Math.round((byteSize ?? 0) / (1024 * 1024)) })}
        </Button>
      </div>
    );
  }

  return (
    <ChunkBoundary onError={onFailed}>
      <Suspense
        fallback={
          <div className="flex h-full items-center justify-center text-white/70">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        }
      >
        <CragRouteModel
          url={url}
          routes={routes}
          editor={null}
          framing={framing}
          selectedRouteId={selectedRouteId}
          viewLimits={viewLimits}
          onSelectRoute={onSelectRoute}
          onError={onFailed}
          showControlsHelp={false}
          className="h-full rounded-none border-0 bg-transparent text-white/70"
        />
      </Suspense>
    </ChunkBoundary>
  );
}
